import type { FormSchema } from '@cli-types/core-engine/schema.js'
import type { PublicFormComponentSourceOptions } from '@cli-types/next-adapter/forms.js'
import { escapeJsx, toPascalCase } from '@cli-utils/text.js'
import { getAllFormSchemaFields } from '@core-engine/schema/field-helpers/form-fields/get-all-form-schema-fields.js'
import { resolveSchemaActionImportPath } from '@next-adapter/config/paths/resolve-schema-action-import-path.js'
import { buildDefaultValues } from '@next-adapter/generators/form-pipeline/form-component-shared/build-default-values.js'
import { buildFieldArrayDecls } from '@next-adapter/generators/form-pipeline/form-component-shared/build-field-array-decls.js'
import { buildFieldErrorHelper } from '@next-adapter/generators/form-pipeline/form-component-shared/build-field-error-helper.js'
import { buildWatchDecls } from '@next-adapter/generators/form-pipeline/form-component-shared/build-watch-decls.js'
import { buildZodFields } from '@next-adapter/generators/form-pipeline/form-component-shared/build-zod-fields.js'
import { getListFields } from '@next-adapter/generators/form-pipeline/form-component-shared/get-list-fields.js'
import { renderFieldsJSX } from '@next-adapter/generators/form-pipeline/form-component-shared/render-fields-jsx.js'
import { renderBuildSingleStepFormComponentSourceClientModuleTemplate } from '@next-adapter/templates/renderers/generators/form-pipeline/form-component-single/build-single-step-form-component-source.js'

export function buildSingleStepFormComponentSource(
  schema: FormSchema,
  options: PublicFormComponentSourceOptions = {}
): string {
  const formName = schema.name
  const pascal = toPascalCase(formName)
  const fields = getAllFormSchemaFields(schema)
  const actionImportPath =
    options.actionImportPath ?? resolveSchemaActionImportPath(formName, options.outputNamespace)

  const zodFields = buildZodFields(fields)
  const defaults = buildDefaultValues(fields)
  const listFields = getListFields(fields)
  const hasListFields = listFields.length > 0
  const { setup: watchSetup } = buildWatchDecls(fields)

  const rawFields = schema.fields || []
  const fieldJSX = renderFieldsJSX(rawFields)

  const submitText = JSON.stringify(schema.submitButtonText || 'Submit')
  const successMessage = escapeJsx(schema.successMessage || 'Form submitted successfully!')
  const redirectUrl = schema.redirectUrl
  const successHandler = redirectUrl
    ? `window.location.href = ${JSON.stringify(redirectUrl)}`
    : 'setSubmitted(true)'

  const rhfImport = hasListFields
    ? `import { Controller, useFieldArray, useForm, type FieldErrors } from 'react-hook-form'`
    : `import { Controller, useForm, type FieldErrors } from 'react-hook-form'`

  const fieldArraySetup = hasListFields ? `\n${buildFieldArrayDecls(listFields)}\n` : ''

  const lucideImport = hasListFields ? `\nimport { Trash2 } from 'lucide-react'` : ''

  return renderBuildSingleStepFormComponentSourceClientModuleTemplate({
    lucideImport: lucideImport,
    rhfImport: rhfImport,
    pascal: pascal,
    actionImportPath: actionImportPath,
    zodFields: zodFields,
    fieldErrorHelper: buildFieldErrorHelper(),
    defaults: defaults,
    fieldArraySetup: fieldArraySetup,
    watchSetup: watchSetup,
    successHandler: successHandler,
    successMessage: successMessage,
    fieldJsx: fieldJSX,
    submitText: submitText
  })
}
